import { WineLotWithClient } from "@/hooks/useWineLots";
import { useVolumeUnit } from "@/hooks/useOrganization";
import { Wine, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { useVarietyColorMap, getVarietyColor } from "@/hooks/useVarietyColorMap";

export type SortField = "code" | "client" | "varietal" | "vintage" | "volume" | "created";
export type SortDirection = "asc" | "desc";

interface LotsTableProps {
  lots: WineLotWithClient[];
  isLoading: boolean;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onRowClick: (lot: WineLotWithClient) => void;
}

function SortIcon({ field, currentField, direction }: {
  field: SortField;
  currentField: SortField;
  direction: SortDirection;
}) {
  if (field !== currentField) {
    return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
  }
  return direction === "asc"
    ? <ArrowUp className="h-4 w-4 ml-1" />
    : <ArrowDown className="h-4 w-4 ml-1" />;
}

function SortableHeader({
  field,
  label,
  currentField,
  direction,
  onSort,
  className = ""
}: {
  field: SortField;
  label: string;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  className?: string;
}) {
  return (
    <th
      className={`px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none ${className}`}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center">
        {label}
        <SortIcon field={field} currentField={currentField} direction={direction} />
      </div>
    </th>
  );
}

export function LotsTable({
  lots,
  isLoading,
  sortField,
  sortDirection,
  onSort,
  onRowClick
}: LotsTableProps) {
  const { volumeAbbrev } = useVolumeUnit();
  const colorMap = useVarietyColorMap();

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card">
        <div className="divide-y">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4">
              <Skeleton className="h-10 w-10 rounded-md" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (lots.length === 0) {
    return null; // Empty state handled by parent
  }

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <table className="w-full">
        <thead className="bg-muted/50 border-b">
          <tr>
            <SortableHeader
              field="code"
              label="Code"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
            />
            <SortableHeader
              field="client"
              label="Client"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
            />
            <SortableHeader
              field="varietal"
              label="Varietal"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
            />
            <SortableHeader
              field="vintage"
              label="Vintage"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              className="text-center"
            />
            <SortableHeader
              field="volume"
              label={`Volume (${volumeAbbrev})`}
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              className="text-right"
            />
            <SortableHeader
              field="created"
              label="Created"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
            />
          </tr>
        </thead>
        <tbody className="divide-y">
          {lots.map((lot) => (
            <tr
              key={lot.id}
              className="hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => onRowClick(lot)}
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-md"
                    style={{ backgroundColor: getVarietyColor(colorMap, lot.varietal) + "1a" }}
                  >
                    <Wine className="h-4 w-4" style={{ color: getVarietyColor(colorMap, lot.varietal) }} />
                  </div>
                  <span className="font-medium">{lot.code}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">
                {lot.client?.name || "—"}
              </td>
              <td className="px-4 py-3 text-sm">
                <div className="flex items-center gap-1.5">
                  <span
                    className="inline-block h-3 w-3 rounded-full flex-shrink-0 border border-border"
                    style={{ backgroundColor: getVarietyColor(colorMap, lot.varietal) }}
                  />
                  {lot.varietal || "—"}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-center">
                {lot.vintage || "—"}
              </td>
              <td className="px-4 py-3 text-sm text-right font-medium">
                {lot.current_volume_l ? Math.round(lot.current_volume_l).toLocaleString() : "0"}
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">
                {format(new Date(lot.created_at), "MMM d, yyyy")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
